import { useState, useEffect, useRef } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function Countdown2Min() {
  const [timeLeft, setTimeLeft] = useState(120); // 2 phút = 120 giây
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (isRunning && !isPaused && timeLeft > 0) {
      intervalRef.current = window.setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            setIsRunning(false);
            setIsPaused(false);
            playAlarm();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isPaused, timeLeft]);

  const playAlarm = () => {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 1);
  };

  const handleStart = () => {
    if (timeLeft === 0) {
      setTimeLeft(120);
    }
    setIsRunning(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleResume = () => {
    setIsPaused(false);
  };

  const handleReset = () => {
    setIsRunning(false);
    setIsPaused(false);
    setTimeLeft(120);
  };

  const handleAddTime = (seconds: number) => {
    setTimeLeft((prev) => Math.min(prev + seconds, 3599));
  };

  const handleSubtractTime = (seconds: number) => {
    setTimeLeft((prev) => Math.max(prev - seconds, 0));
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const progress = ((120 - timeLeft) / 120) * 100;

  const formatTime = (num: number) => String(num).padStart(2, '0');

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-orange-50">
      <Navbar />
      
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-rose-600 rounded-full mb-6">
              <i className="ri-timer-flash-line text-4xl text-white"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
              Đồng Hồ Đếm Ngược 2 Phút
            </h1>
            <p className="text-xl text-gray-600">
              Hoàn hảo cho các bài tập nhanh và thời gian nghỉ ngắn
            </p>
          </div>

          {/* Main Timer Display */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100 mb-8">
            {/* Progress Ring */}
            <div className="relative w-80 h-80 mx-auto mb-12">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="160"
                  cy="160"
                  r="140"
                  stroke="#fee2e2"
                  strokeWidth="12"
                  fill="none"
                />
                <circle
                  cx="160"
                  cy="160"
                  r="140"
                  stroke="url(#gradient2min)"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 140}`}
                  strokeDashoffset={`${2 * Math.PI * 140 * (1 - progress / 100)}`}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
                />
                <defs>
                  <linearGradient id="gradient2min" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#f97316" />
                    <stop offset="100%" stopColor="#f43f5e" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Time Display */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-7xl md:text-8xl font-bold text-gray-900 tracking-tight">
                  {formatTime(minutes)}
                  <span className="text-orange-600 animate-pulse">:</span>
                  {formatTime(seconds)}
                </div>
                <div className="text-lg text-gray-500 mt-4 font-medium">
                  {timeLeft === 0 ? 'Hết giờ!' : isRunning && !isPaused ? 'Đang chạy...' : isPaused ? 'Tạm dừng' : 'Sẵn sàng'}
                </div>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {!isRunning || isPaused ? (
                <button
                  onClick={isPaused ? handleResume : handleStart}
                  className="whitespace-nowrap px-8 py-4 bg-gradient-to-r from-orange-500 to-rose-600 text-white text-lg font-semibold rounded-xl hover:from-orange-600 hover:to-rose-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 cursor-pointer flex items-center gap-3"
                >
                  <i className={`${isPaused ? 'ri-play-line' : 'ri-play-circle-line'} text-2xl`}></i>
                  {isPaused ? 'Tiếp tục' : 'Bắt đầu'}
                </button>
              ) : (
                <button
                  onClick={handlePause}
                  className="whitespace-nowrap px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-white text-lg font-semibold rounded-xl hover:from-amber-600 hover:to-amber-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 cursor-pointer flex items-center gap-3"
                >
                  <i className="ri-pause-circle-line text-2xl"></i>
                  Tạm dừng
                </button>
              )}
              
              <button
                onClick={handleReset}
                className="whitespace-nowrap px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white text-lg font-semibold rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 cursor-pointer flex items-center gap-3"
              >
                <i className="ri-restart-line text-2xl"></i>
                Đặt lại
              </button>
            </div>

            {/* Quick Time Adjustment */}
            {!isRunning && (
              <div className="border-t border-gray-200 pt-8">
                <h3 className="text-lg font-semibold text-gray-700 mb-4 text-center">
                  Điều chỉnh thời gian nhanh
                </h3>
                <div className="flex flex-wrap justify-center gap-3">
                  <button
                    onClick={() => handleSubtractTime(30)}
                    disabled={timeLeft < 30}
                    className="whitespace-nowrap px-6 py-3 bg-red-50 text-red-600 font-semibold rounded-lg hover:bg-red-100 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <i className="ri-subtract-line"></i>
                    -30 giây
                  </button>
                  <button
                    onClick={() => handleSubtractTime(15)}
                    disabled={timeLeft < 15}
                    className="whitespace-nowrap px-6 py-3 bg-red-50 text-red-600 font-semibold rounded-lg hover:bg-red-100 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <i className="ri-subtract-line"></i>
                    -15 giây
                  </button>
                  <button
                    onClick={() => handleAddTime(15)}
                    className="whitespace-nowrap px-6 py-3 bg-orange-50 text-orange-600 font-semibold rounded-lg hover:bg-orange-100 transition-all duration-300 cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-add-line"></i>
                    +15 giây
                  </button>
                  <button
                    onClick={() => handleAddTime(30)}
                    className="whitespace-nowrap px-6 py-3 bg-orange-50 text-orange-600 font-semibold rounded-lg hover:bg-orange-100 transition-all duration-300 cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-add-line"></i>
                    +30 giây
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Features Info */}
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-orange-100 rounded-full mx-auto mb-4">
                <i className="ri-flashlight-line text-2xl text-orange-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Nhanh chóng</h3>
              <p className="text-gray-600">Hoàn hảo cho các tác vụ ngắn</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-4">
                <i className="ri-alarm-warning-line text-2xl text-rose-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Cảnh báo</h3>
              <p className="text-gray-600">Âm thanh nhắc nhở rõ ràng</p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-amber-100 rounded-full mx-auto mb-4">
                <i className="ri-timer-2-line text-2xl text-amber-600"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Tiện lợi</h3>
              <p className="text-gray-600">Dễ dàng sử dụng và điều chỉnh</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}